import axios from 'axios'
import { useState } from 'react'
import { toast } from 'sonner'

import { inviteUser } from '@/api/api'
import type { UserListItem } from '@/api/type'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onInvited: (user: UserListItem) => void
}

/** 招待制のユーザー事前登録。登録したメールの Google アカウントでサインインできるようになる */
export const InviteUserDialog = ({ open, onOpenChange, onInvited }: Props) => {
  const [email, setEmail] = useState('')
  const [customName, setCustomName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEmail('')
      setCustomName('')
    }
    onOpenChange(next)
  }

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      const user = await inviteUser({ email: email.trim(), custom_name: customName.trim() || null })
      onInvited(user)
      toast.success('招待しました。本人に Google でサインインしてもらってください')
      handleOpenChange(false)
    } catch (error) {
      console.error('Failed to invite user:', error)
      const detail = axios.isAxiosError<{ detail?: unknown }>(error) ? error.response?.data?.detail : undefined
      toast.error(typeof detail === 'string' ? detail : '招待に失敗しました')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>ユーザーを招待</DialogTitle>
            <DialogDescription>
              招待したメールアドレスの Google アカウントでサインインできるようになります。権限は招待後に設定できます。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="invite-email">メールアドレス</Label>
            <Input
              id="invite-email"
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@gmail.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-name">カスタムネーム（任意）</Label>
            <Input id="invite-name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting || !email.trim()}>
              {submitting ? '招待中...' : '招待'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
